import React, { useEffect, useMemo, useState } from 'react'
import { snakeize } from 'casing'
import { useDispatch, useSelector } from 'react-redux'
import {
  FileLinks,
  FileUploadStatusType,
  FileUploadV2,
  FileUploadDivType,
} from '@labrav/react-components'
import { API } from '../../api'
import {
  deleteAsset as defaultDeleteAsset,
  getPreSignedUrl,
} from '../../api/marketplaceAssets'
import { DefaultNowType } from '../../utils/dateTimeNow'
import { uploadFilesProcessing } from '../../utils/uploadFilesProcessing'
import { isLoading } from '../../utils/loadingState'
import { flyoutS3Url } from '../../utils/flyoutConstants'
import {
  LoadingTypes,
  type ComponentLoadingState,
} from '../../modules/loading/reducer'
import { startLoading, stopLoading } from '../../modules/loading/actions'
import { updateAppAlert } from '../../modules/appAlert/actions'

export const getFileLinksFromUrl = (
  fileUrls: string[],
  showRealFileName?: boolean
) => {
  if (!fileUrls?.length) {
    return []
  }
  return fileUrls.map(link => {
    const fileNames = link.split('/')
    const name = showRealFileName
      ? fileNames[fileNames.length - 1]?.split('-file-')[1]
      : fileNames[fileNames.length - 1]
    return {
      name,
      link,
      loadingPercentage: 100,
      status: 'success' as FileUploadStatusType,
    }
  })
}

export type DeleteAssetFn = (
  partnerId: string,
  data: Record<string, string>
) => Promise<unknown>

export type GetSignedUrlFn = (
  partnerId: string,
  path: string,
  bucketName?: string
) => Promise<{ data: unknown }>

export interface FileUploadWithLoaderProps {
  keyName: string
  partnerId: string
  assetType: string
  folder: string
  fileUploadDescription: string
  quantity: number
  fileUploadType: string
  preProcessBeforeUpload?: (file: File) => Promise<boolean>
  unit: string
  uploadFileSize: number
  acceptedFormats: string[]
  existingFileLinks: string[]
  updateUrl: (fileUrls: string, index?: number) => void
  /** Called after S3 upload succeeds (and optional post-upload validation passes). */
  onUploadComplete?: (fileUrl: string, file: File) => void
  deleteUrl: (index: number) => void
  defaultNow: DefaultNowType
  fileUploadButtonText: string
  fileFormatIncorrectText: string
  fileUploadContainerTitle: string
  bucketName?: string
  isMultiFileUpload?: boolean
  disabled?: boolean
  updateErr?: (error: string) => void
  uploadError?: string
  postUploadValidator?: (fileUrl: string) => Promise<boolean>
  onPostUploadValidatedSuccess?: (fileUrl: string) => void
  onPostUploadValidatedError?: (fileUrl: string) => void
  aspectRatios?: string[]
  maxPixels?: number
  minPixels?: number
  viewOnly?: boolean
  getSignedUrl?: GetSignedUrlFn
  deleteAsset?: DeleteAssetFn
  s3Url?: string
  checkDuplicate?: boolean
  showRealFileName?: boolean
  onRetryFile?: () => void
  truncateFileName?: boolean
}

export const FileUploadWithLoader = React.memo(
  ({
    keyName,
    partnerId,
    assetType,
    preProcessBeforeUpload,
    folder,
    fileUploadDescription,
    unit,
    quantity,
    fileUploadType,
    uploadFileSize,
    acceptedFormats,
    updateUrl,
    onUploadComplete,
    deleteUrl,
    existingFileLinks,
    defaultNow,
    fileUploadButtonText,
    fileFormatIncorrectText,
    fileUploadContainerTitle,
    bucketName,
    isMultiFileUpload = false,
    disabled = false,
    updateErr,
    uploadError,
    postUploadValidator,
    onPostUploadValidatedSuccess,
    onPostUploadValidatedError,
    aspectRatios,
    maxPixels,
    minPixels,
    viewOnly,
    getSignedUrl = getPreSignedUrl,
    deleteAsset = defaultDeleteAsset,
    s3Url = flyoutS3Url,
    checkDuplicate,
    showRealFileName,
    onRetryFile,
    truncateFileName = true,
  }: FileUploadWithLoaderProps) => {
    const updatedExistingFileLinks = useMemo(() => {
      return getFileLinksFromUrl(existingFileLinks, showRealFileName)
    }, [existingFileLinks, showRealFileName])

    const [existingFileLinkList, setExistingFileLinkList] = useState<
      FileLinks[]
    >([])
    const [files, setFiles] = useState<FileLinks[]>([])

    const filesLoading = useSelector(
      (state: { loading: ComponentLoadingState }) =>
        isLoading(state.loading)(LoadingTypes.FILE_PROCESSING_LOADER)
    )
    useEffect(() => {
      if (!filesLoading) {
        setExistingFileLinkList(updatedExistingFileLinks)
      }
    }, [updatedExistingFileLinks, filesLoading])
    const dispatch = useDispatch()
    const [errorText, setErrorText] = useState<string>('')
    const deleteFile = async (index: number) => {
      setFiles([])
      const updatedFileList = [...existingFileLinkList]
      updatedFileList[index].status = 'deleting' as FileUploadStatusType
      await setExistingFileLinkList([...updatedFileList])
      await dispatch(startLoading(LoadingTypes.FILE_PROCESSING_LOADER))
      deleteAsset(
        partnerId,
        snakeize({ objectUrl: existingFileLinkList[index].link })
      )
        .then(() => {
          deleteUrl(index)
          setErrorText('')
        })
        .catch(() => {
          setErrorText('Error deleting file.')
          const updatedFileListWithErr = [...existingFileLinkList]
          updatedFileListWithErr[index].status =
            'success' as FileUploadStatusType
          setExistingFileLinkList([...updatedFileList])
        })
        .finally(() => {
          dispatch(stopLoading(LoadingTypes.FILE_PROCESSING_LOADER))
        })
    }
    const handleRetryFile = (index: number, key: FileUploadDivType) => {
      if (key === FileUploadDivType.FILE) {
        setFiles(prev => prev.filter((_, i) => i !== index))
        setErrorText('')

        if (onRetryFile) {
          onRetryFile()
        }
        setTimeout(() => {
          const inputElement = document.getElementById(
            `file-input-${keyName}`
          ) as HTMLInputElement | null
          inputElement?.click()
        }, 0)
      }
    }
    const uploadFile = async (
      key: string,
      index: number,
      presignedUrl: string,
      formData: FormData,
      filePathObjects: Record<string, File>
    ) => {
      await API.post(`${presignedUrl}`, formData, {
        headers: {
          Authorization: '',
        },
        onUploadProgress(progressEvent) {
          const loaded = progressEvent.loaded
          const total = (progressEvent as { lengthComputable?: boolean })
            ?.lengthComputable
            ? progressEvent.total || 0
            : filePathObjects[key].size
          const rawPercentage =
            total > 0 ? Math.round((loaded / total) * 100) : 0
          const percentage = Math.min(rawPercentage, 95)
          const filesObject = [...files]
          filesObject[index].loadingPercentage = percentage
          setFiles([...filesObject])
        },
      })
        .then(async () => {
          const fullUrl = `${s3Url}/${key}`

          if (postUploadValidator) {
            const isValid = await postUploadValidator(fullUrl)
            if (!isValid) {
              const filesObject = [...files]
              filesObject[index].status = 'error' as FileUploadStatusType
              setFiles(filesObject)

              if (onPostUploadValidatedError) {
                onPostUploadValidatedError(fullUrl)
              }
              return
            }
          }
          if (onPostUploadValidatedSuccess) {
            onPostUploadValidatedSuccess(fullUrl)
          }

          onUploadComplete?.(fullUrl, filePathObjects[key])
          updateUrl(fullUrl, existingFileLinks.length)
          const updatedFile = [...files]
          updatedFile.splice(index, 1)
          setFiles([...updatedFile])
        })
        .catch(() => {
          const filesObject = [...files]
          filesObject[index].status = 'error' as FileUploadStatusType
          setFiles(filesObject)
        })
    }
    useEffect(() => {
      if (files.length > 0 && !filesLoading) {
        dispatch(startLoading(LoadingTypes.FILE_PROCESSING_LOADER))
        const filesToBeUploaded = files.map(original => original.file) as File[]
        const { path, filePathObjects } = uploadFilesProcessing({
          partnerId,
          assetType,
          folder,
          filesToBeUploaded,
          defaultNow,
          showRealFileName,
        })
        getSignedUrl(partnerId, path, bucketName)
          .then(async ({ data }) => {
            const payload = data as {
              [index: number]: {
                predisigned_url_data?: never
                presigned_url_data: Record<
                  string,
                  { url: string; fields: Record<string, string> }
                >
              }
            }
            const signedUrlObjects = payload[0].presigned_url_data
            await Object.keys(signedUrlObjects).map(
              async (key: string, index: number) => {
                const filesObject = [...files]
                const fileNames = key.split('/')
                filesObject[index].name = fileNames[fileNames.length - 1]
                setFiles(filesObject)
                const predisigned = signedUrlObjects[key]
                const formData = new FormData()
                Object.keys(predisigned.fields).forEach((field: string) => {
                  formData.append(field, predisigned.fields[field])
                })
                formData.append('file', filePathObjects[key])
                await uploadFile(
                  key,
                  index,
                  predisigned.url,
                  formData,
                  filePathObjects
                )
              }
            )
          })
          .catch(error => {
            console.error('error while uploading file', error)
          })
          .finally(() => {
            dispatch(stopLoading(LoadingTypes.FILE_PROCESSING_LOADER))
          })
      }
    }, [files.length])
    const handleFileUpload = async (filesToBeUploaded: File[]) => {
      if (!filesToBeUploaded?.length) {
        return
      }
      let errors: boolean[] = []
      if (preProcessBeforeUpload) {
        errors = await Promise.all(
          filesToBeUploaded.map(preProcessBeforeUpload)
        )
      }
      if (!errors.includes(true)) {
        const fileObjects = filesToBeUploaded.map(file => ({
          file,
          name: '',
          link: '',
          loadingPercentage: 0,
          status: 'uploading' as FileUploadStatusType,
        }))
        setFiles(fileObjects)
      }
    }
    const fileList = files?.map(fileItem => {
      return {
        ...fileItem,
        name: showRealFileName
          ? fileItem?.name?.split('-file-')[1]
          : fileItem?.name,
      }
    })

    const handleUpdateErr = (error: string) => {
      if (error?.includes('is already uploaded.')) {
        dispatch(
          updateAppAlert({
            message: error,
            messageType: 'ERROR',
            autoClose: true,
          })
        )
      } else {
        updateErr && updateErr(error)
      }
    }

    return (
      <FileUploadV2
        keyName={keyName}
        deleteFile={deleteFile}
        addFile={handleFileUpload}
        fileLinks={[...fileList]}
        description={fileUploadDescription}
        quantity={quantity}
        unit={unit}
        typeUpload={fileUploadType}
        uploadFileSize={uploadFileSize}
        acceptedFormats={acceptedFormats}
        existingFileLinks={[...existingFileLinkList]}
        deletionHeadingMessage={'Remove file'}
        fileUploadButtonText={fileUploadButtonText}
        fileFormatIncorrectText={fileFormatIncorrectText}
        fileDeletionErrorText={errorText}
        fileUploadContainerTitle={fileUploadContainerTitle}
        isMultipleUpload={isMultiFileUpload}
        disabled={disabled}
        updateErr={handleUpdateErr}
        uploadError={uploadError}
        aspectRatios={aspectRatios}
        maxPixels={maxPixels}
        minPixels={minPixels}
        viewOnly={viewOnly}
        checkDuplicate={checkDuplicate}
        onRetryFile={onRetryFile ? handleRetryFile : undefined}
        truncateFileName={truncateFileName}
      />
    )
  }
)
